import { useMutation } from '@apollo/react-hooks';
import { CREATE_ADMISSION_STEP_DATA } from './CREATE_ADMISSION_STEP_DATA.gql';
import type { AdmissionForm } from '../types';
import type {
  CreateDsrcAdmissionInput,
  Dsrc_Decision_Type,
  EntranceRequirement,
} from '../../../../../../graphql/graphql';
import type { CreateAdmissionStepData } from './types';
import { DSRC_DECISION_TYPE, OFFER_TYPE } from '../../constants';
import { isValidDate } from '../../../../../../utils/date';

export const useCreateAdmissionStepData = (
  refetch?: () => void,
): CreateAdmissionStepData => {
  const [createAdmissionStepData] = useMutation(CREATE_ADMISSION_STEP_DATA, {
    onCompleted: () => {
      if (refetch !== undefined) refetch();
    },
  });

  const convertAdmissionForm = (
    applicationId: string,
    admissionForm: AdmissionForm,
  ): CreateDsrcAdmissionInput => {
    const {
      admissionDecision,
      returnDestination,
      conditions,
      offerType,
      plagiarismNotFound,
      tuitionAssistantshipType,
      stipendApproval,
      conditionFulfillingDeadline,
      ...others
    } = admissionForm;

    const convertedConditions = Object.keys(conditions).filter(key => {
      return conditions?.[key as EntranceRequirement]?.ticked;
    }) as EntranceRequirement[];

    const conditionDetails = convertedConditions?.map(key => {
      const useCustomDetail = conditions[key].detailType === 'custom';
      const customDetail = conditions[key].detail;
      const detailItem = {
        requirement: key,
        ...(useCustomDetail ? { detail: customDetail } : {}),
      };
      return detailItem;
    });

    let dsrcDecision: unknown = admissionDecision;
    if (admissionDecision === DSRC_DECISION_TYPE.RETURN) {
      dsrcDecision = returnDestination;
    }

    const isFirmOffer = offerType === OFFER_TYPE.FIRM;
    const plagiarismFound = !plagiarismNotFound;

    const convertedInput = {
      applicationId,
      conditionDetails,
      conditions: convertedConditions,
      dsrcDecision: dsrcDecision as Dsrc_Decision_Type,
      isFirmOffer,
      plagiarismFound,
      tuitionAssistantshipType: tuitionAssistantshipType || undefined,
      stipendApproval: stipendApproval === 'true',
      // empty string for conditionFulfillingDeadline field will cause invalidate error in backend
      conditionFulfillingDeadline: isValidDate(conditionFulfillingDeadline)
        ? conditionFulfillingDeadline
        : null,
      ...others,
    };
    return convertedInput;
  };

  return { createAdmissionStepData, convertAdmissionForm };
};
